/**
 * VTO Bridge — núcleo puro.
 *
 * Sin DOM, sin efectos secundarios: todo lo que decide algo vive aquí para poder
 * probarse con `node --test`. El cableado del DOM está en vto-bridge.js.
 *
 * Se carga como script clásico en el tema (define window.VtoBridgeCore) y como
 * módulo CommonJS en las pruebas (module.exports).
 *
 * Spec: docs/superpowers/specs/2026-08-15-vto-shopify-port-design.md §6
 */
(function (root) {
  'use strict';

  /* El origen contra el que se validan los mensajes del iframe. DEBE coincidir con el
     `data-vto-base` de vto-modal.liquid y vto-outfit.liquid: si se desincronizan, el puente
     rechaza TODOS los mensajes en silencio -- ni carrito, ni descarga, ni cierre, y sin
     ningún error visible. Hay una prueba que falla si dejan de coincidir. */
  var VTO_ORIGIN = 'https://probador-virtual.ivonne.com';
  var DEVICE_ID_KEY = 'vto_web_device_id';

  /**
   * El iframe manda el variant id como gid o como número; el carrito de Shopify
   * quiere el numérico pelón.
   */
  function normalizeVariantId(raw) {
    if (raw === null || raw === undefined) return '';
    return String(raw).replace(/\D/g, '');
  }

  /**
   * Sin esto, cualquier iframe ya presente en la tienda (Rebuy, Nosto, Klaviyo,
   * Alia) podría meter cosas al carrito. Se exige origen Y fuente.
   */
  function isTrustedVtoMessage(event, expectedSource) {
    if (!event || event.origin !== VTO_ORIGIN) return false;
    if (!expectedSource || event.source !== expectedSource) return false;
    var data = event.data;
    if (!data || typeof data.type !== 'string' || data.type === '') return false;
    return true;
  }

  /**
   * Las claves vacías SÍ se mandan: la app de Tapcart las manda así y el servidor
   * las registra en analítica. Filtrarlas rompería la comparabilidad app/web.
   */
  function buildVtoUrl(base, params) {
    var keys = Object.keys(params || {});
    if (keys.length === 0) return base;
    var pairs = keys.map(function (key) {
      var value = params[key];
      if (value === null || value === undefined) value = '';
      return encodeURIComponent(key) + '=' + encodeURIComponent(String(value));
    });
    return base + '?' + pairs.join('&');
  }

  /**
   * Antes vivía duplicada (5 líneas idénticas) en vto-bridge.js y vto-outfit-bridge.js.
   * Recibe `deps` inyectable para poder probarse: en pruebas se le pasa un `crypto` con
   * o sin `randomUUID`, en vez de depender del global real. Sin argumento usa `window`
   * (y en Node, donde no hay `window`, `deps` queda `undefined` y cae al respaldo sin
   * lanzar: `deps.crypto` con `deps` undefined se protege con el `&&` de abajo).
   */
  function randomId(deps) {
    // Bare `window` revienta con ReferenceError en Node si se accede sin el `typeof`
    // (a diferencia de una propiedad de `undefined`, que sólo truena al usarla); por
    // eso el respaldo a `window` va guardado igual que en safeLocalStorage.
    var crypto = deps ? deps.crypto : (typeof window === 'undefined' ? undefined : window.crypto);
    if (crypto && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'vto-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  /**
   * En Chrome con "bloquear todas las cookies", el simple ACCESO a la propiedad
   * `window.localStorage` (no sólo leer/escribir en ella) ya lanza `SecurityError`.
   * Esto aísla esa lectura para que quien llame (vto-bridge.js) nunca reviente antes
   * de fijar `frame.src`. Devuelve null si no hay storage utilizable (o no hay
   * `window`, como en las pruebas de Node).
   */
  function safeLocalStorage() {
    try {
      return typeof window === 'undefined' ? null : window.localStorage;
    } catch (error) {
      return null;
    }
  }

  /**
   * En iframes cross-origin, el ITP de iOS puede bloquear localStorage. En ese
   * caso se devuelve un id efímero en vez de reventar. `storage` puede venir en
   * null (ver safeLocalStorage) cuando ni siquiera se pudo leer la propiedad.
   */
  function getOrCreateDeviceId(storage, uuidFn) {
    if (!storage) return uuidFn();
    try {
      var existing = storage.getItem(DEVICE_ID_KEY);
      if (existing) return existing;
      var fresh = uuidFn();
      storage.setItem(DEVICE_ID_KEY, fresh);
      return fresh;
    } catch (error) {
      return uuidFn();
    }
  }

  /**
   * El filtro image_url de Liquid devuelve URLs protocol-relative (//cdn...);
   * el servidor VTO necesita una absoluta para poder descargarla.
   */
  function absoluteImageUrl(src, origin) {
    if (!src) return '';
    var text = String(src);
    if (text.indexOf('//') === 0) {
      var scheme = String(origin).split(':')[0];
      return scheme + ':' + text;
    }
    return text;
  }

  /**
   * Serializa los add-to-cart: mientras uno está en vuelo, los siguientes se encolan.
   *
   * Existe porque el armador agrega varias prendas seguidas (es su caso de uso) y dos add
   * solapados dejan el contador del carrito desfasado -- el `cart:change` que gana la carrera
   * puede llevar un carrito leído antes de que el otro se confirmara -- además de que Shopify
   * puede responder 429/422 a mutaciones concurrentes.
   *
   * El vigilante no es opcional: ProductForm.onSubmit_fn del tema es async, y si el fetch a
   * cart/add.js se rechaza a nivel de red la promesa queda rechazada SIN emitir `variant:add`
   * ni `cart:error`. Sin vigilante la cola se quedaría muerta.
   *
   * DECISIÓN CLAVE: al dispararse, el vigilante NO encadena el siguiente add: vacía la cola y
   * deja la cola en reposo. Dos razones. Una, tras un timeout el estado del carrito es
   * desconocido -- la petición puede aterrizar tarde -- y apilar mutaciones encima puede
   * duplicar líneas. Dos, el camino de ÉXITO SÍ trae identidad -- `variant:add` de theme.js
   * mete en `detail.items` las líneas que devolvió `cart/add.js`, cada una con su variant id --
   * y se usa para correlacionar: un `settle` de éxito que no incluya el variantId en vuelo se
   * descarta por pertenecer al add abandonado. Sólo `cart:error` carece de identidad (su
   * payload es sólo `{error}`), así que ahí se acepta sin más para el que esté en vuelo.
   * Dejando la cola en reposo, la guarda `if (!busy) return` descarta además cualquier
   * respuesta tardía que llegue cuando NADA está en vuelo. Una versión anterior intentó llevar
   * la cuenta de respuestas a descartar y se rompía justo en el caso motivador: cuando la
   * respuesta no llegaba NUNCA, la cuenta la pagaba el `settle` legítimo del add siguiente y el
   * desfase se propagaba en cadena.
   *
   * @param {object} deps
   * @param {(variantId: string) => void} deps.submit                   lanza el add
   * @param {(message: string|null, batchFailed: boolean) => void} deps.onSettle
   * @param {string} deps.timeoutMessage                                aviso cuando vence el vigilante
   * @param {number} deps.timeoutMs
   * @param {(fn: Function, ms: number) => any} deps.setTimer
   * @param {(id: any) => void} deps.clearTimer
   */
  function createAddQueue(deps) {
    var queue = [];
    var busy = false;
    var timer = null;
    // Si alguna prenda del lote falló, un éxito posterior NO debe borrar ese aviso: la
    // compradora tiene que enterarse de la que no entró aunque las siguientes sí entren.
    var batchFailed = false;
    // El variantId del add actualmente en vuelo, para correlacionar el camino de éxito (ver
    // settle más abajo). null cuando no hay nada en vuelo.
    var inFlightId = null;

    function stopTimer() {
      if (timer === null) return;
      deps.clearTimer(timer);
      timer = null;
    }

    function start(variantId) {
      busy = true;
      inFlightId = variantId;
      timer = deps.setTimer(onTimeout, deps.timeoutMs);
      deps.submit(variantId);
    }

    function onTimeout() {
      if (!busy) return;
      timer = null;
      busy = false;
      inFlightId = null;
      queue.length = 0;
      // No se toca `batchFailed` aquí: tras el timeout la cola queda en reposo, y el único
      // camino a un `finish()` posterior es un `push()` nuevo, que resetea `batchFailed = false`
      // antes de arrancar -- asignarlo aquí no tendría ningún efecto observable.
      deps.onSettle(deps.timeoutMessage, true);
    }

    function finish(message) {
      if (!busy) return;
      stopTimer();
      busy = false;
      inFlightId = null;
      if (message) batchFailed = true;
      var next = queue.length > 0 ? queue.shift() : null;
      // Arrancar el siguiente ANTES de notificar: si onSettle reentrara con un push, lo
      // encolaría en vez de lanzarlo, y no pisaría el temporizador recién armado.
      if (next !== null) start(next);
      deps.onSettle(message || null, batchFailed);
    }

    return {
      push: function (variantId) {
        if (busy) {
          queue.push(variantId);
          return;
        }
        batchFailed = false;
        start(variantId);
      },
      /**
       * settle(message: string|null, addedVariantIds?: string[])
       *
       * Sólo el camino de ÉXITO trae identidad: theme.js:2823-2829 mete en detail.items las
       * líneas que devolvió cart/add.js, cada una con su variant id. Si esos ids no incluyen
       * el que tenemos en vuelo, esta respuesta es de un add que el vigilante ya dio por
       * perdido y que llegó tarde. Aceptarla cerraría en falso el reintento, apagaría SU
       * vigilante, y escondería para siempre un fallo real suyo -- pérdida silenciosa de
       * carrito, peor que la falsa alarma que este diseño vino a evitar.
       *
       * cart:error NO trae identidad (su payload es sólo {error}), así que ahí no se puede
       * correlacionar y se acepta para el que esté en vuelo. Es el sesgo correcto: ante la
       * duda, mostrar un error de más antes que ocultar uno de menos.
       */
      settle: function (message, addedVariantIds) {
        if (!message && inFlightId !== null && Array.isArray(addedVariantIds) && addedVariantIds.length > 0) {
          if (addedVariantIds.indexOf(inFlightId) === -1) return;
        }
        finish(message || null);
      },
      isBusy: function () { return busy; },
      pending: function () { return queue.length; }
    };
  }

  var api = {
    VTO_ORIGIN: VTO_ORIGIN,
    DEVICE_ID_KEY: DEVICE_ID_KEY,
    normalizeVariantId: normalizeVariantId,
    randomId: randomId,
    isTrustedVtoMessage: isTrustedVtoMessage,
    buildVtoUrl: buildVtoUrl,
    getOrCreateDeviceId: getOrCreateDeviceId,
    absoluteImageUrl: absoluteImageUrl,
    safeLocalStorage: safeLocalStorage,
    createAddQueue: createAddQueue,
  };

  // Se exige la AUSENCIA de `window` antes de mirar `module`: en la PDP conviven
  // jQuery, Swiper, el webbridge de Tapcart, Heap y lo que inyecte GTM, y cualquier
  // UMD de esos que filtre un `module` global haría que este core se exportara ahí
  // en vez de a `window.VtoBridgeCore`. En Node (las pruebas) no hay `window`, así
  // que el comportamiento de `require(...)` no cambia.
  if (typeof window === 'undefined' && typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    root.VtoBridgeCore = api;
  }
})(typeof self !== 'undefined' ? self : this);
